import React from 'react';
import { css } from '@emotion/react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

import { colors } from '@/styles';

import { SectionSubheader } from './blocks';
import MiniCalendar, { fromKey, toKey } from './MiniCalendar';
import WeekdayPicker from './WeekdayPicker';
import { SCHEDULE_OPTIONS, SCHEDULE_TYPES, firstRunKey, skipsShortMonths, withType } from './schedule';

// The MUI X pickers render an @mui/material TextField, which reads the v5 default palette — the
// app's green lives in the v4 theme and never reaches it, so they focus Material blue. Scoped to
// these rather than fixed with a v5 ThemeProvider, which would restyle every other picker in the
// app. `:not(.Mui-error)` so a field in error still reads red while focused.
const GREEN_FOCUS = {
    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.greens.primary,
    },
    '& .MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
        color: colors.greens.primary,
    },
};

const ROW = css`
    margin-bottom: 20px;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 16px;
    flex: 0;
    flex-basis: 0;
`;

// An hour is a short value; these don't need the width the pickers beside them do.
const HOUR_FIELD = css`
    flex: 0 0 180px;
`;

const WARNING = css`
    color: ${colors.oranges.primary};
    margin-bottom: 20px;
`;

const hourValue = (hour) => new Date().setHours(hour || 0, 0, 0, 0);

// The calendar goes under the dropdown rather than beside it; the badges are one short row and
// stay beside it.
const usesCalendar = (schedule) => [SCHEDULE_TYPES.MONTHLY, SCHEDULE_TYPES.ONE_OFF].includes(schedule.type);

// Under the start date. A one-off schedule's start follows its dates; a repeating one says when it
// first runs if that isn't the start date itself, since that is the date it reopens on.
const startDateHelper = (schedule, todayKey) => {
    if (schedule.type === SCHEDULE_TYPES.ONE_OFF) return 'Starts on the first date picked';

    const firstRun = firstRunKey(schedule);
    if (!firstRun || firstRun === schedule.startKey || schedule.startKey < todayKey) return '';

    return `First runs ${format(fromKey(firstRun), 'EEE, MMM d')}`;
};

const toggleIn = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

// The picker the schedule's type needs: weekday badges, or a month to pick dates or days from.
const SchedulePicker = ({ schedule, setSchedule, todayKey, error }) => {
    switch (schedule.type) {
        case SCHEDULE_TYPES.MONTHLY:
            // Days of the month, not dates: the 15th lights up in whichever month is showing.
            return (
                <MiniCalendar
                    isPicked={(key) => schedule.monthDays.includes(Number(key.slice(8, 10)))}
                    onToggle={(key) =>
                        setSchedule((prev) => ({
                            ...prev,
                            monthDays: toggleIn(prev.monthDays, Number(key.slice(8, 10))),
                        }))
                    }
                    referenceKey={schedule.startKey}
                    caption="Days of the month the resource runs"
                    error={error}
                />
            );

        case SCHEDULE_TYPES.ONE_OFF:
            // Real dates, from today on. The start date follows the first of them.
            return (
                <MiniCalendar
                    isPicked={(key) => schedule.dates.includes(key)}
                    onToggle={(key) =>
                        setSchedule((prev) => {
                            const dates = toggleIn(prev.dates, key).sort();
                            return { ...prev, dates, startKey: dates[0] || todayKey };
                        })
                    }
                    minKey={todayKey}
                    referenceKey={schedule.dates[0] || todayKey}
                    caption="Dates the resource runs"
                    error={error}
                />
            );

        default:
            return (
                <WeekdayPicker
                    days={schedule.days}
                    error={error}
                    onToggle={(dayName) =>
                        setSchedule((prev) => ({ ...prev, days: { ...prev.days, [dayName]: !prev.days?.[dayName] } }))
                    }
                />
            );
    }
};

// When, and how often, the resource runs — plus the delivery window it offers on those days.
//
//   Row 1  Start date · Timeframe start · Timeframe end
//   Row 2  Recurring · weekday badges
//          or, for Monthly and Does not repeat, a month calendar under the dropdown
const ScheduleSection = ({
    schedule,
    setSchedule,
    scheduleError,
    isNew,
    todayKey,
    updated,
    setUpdates,
    setupErrors,
}) => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid direction="row" container css={ROW}>
            <Grid
                item
                css={css`
                    flex: 1 1 auto;
                    min-width: 0;
                `}
            >
                <DatePicker
                    label="Start date"
                    value={schedule.startKey ? fromKey(schedule.startKey) : null}
                    onChange={(date) => {
                        if (date && !Number.isNaN(date.getTime())) {
                            setSchedule((prev) => ({ ...prev, startKey: toKey(date) }));
                        }
                    }}
                    // A one-off schedule starts on its first date, so the field follows the calendar.
                    disabled={schedule.type === SCHEDULE_TYPES.ONE_OFF}
                    minDate={isNew ? fromKey(todayKey) : undefined}
                    slotProps={{
                        textField: {
                            size: 'small',
                            fullWidth: true,
                            sx: GREEN_FOCUS,
                            helperText: startDateHelper(schedule, todayKey),
                        },
                    }}
                />
            </Grid>
            <Grid item css={HOUR_FIELD}>
                <TimePicker
                    label="Timeframe Start"
                    views={['hours']}
                    value={hourValue(updated?.timeframe_start)}
                    onChange={(e) => {
                        const hours = e ? new Date(e).getHours() : 0;
                        setUpdates((prev) => ({ ...prev, timeframe_start: hours }));
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: GREEN_FOCUS } }}
                />
            </Grid>
            <Grid item css={HOUR_FIELD}>
                <TimePicker
                    label="Timeframe End"
                    views={['hours']}
                    value={hourValue(updated?.timeframe_end)}
                    onChange={(e) => {
                        const hours = e ? new Date(e).getHours() : 0;
                        setUpdates((prev) => ({ ...prev, timeframe_end: hours }));
                    }}
                    // `error` is not a v6 TimePicker prop — it goes through the textField slot. From
                    // setupErrors, so it blocks Save exactly when it shows.
                    slotProps={{
                        textField: {
                            size: 'small',
                            fullWidth: true,
                            sx: GREEN_FOCUS,
                            error: !!setupErrors?.timeframe,
                            helperText: setupErrors?.timeframe ? 'End must be after start' : '',
                        },
                    }}
                />
            </Grid>
        </Grid>

        <Grid direction="row" container css={ROW}>
            <Grid
                item
                css={css`
                    flex: 0 0 200px;
                `}
            >
                <TextField
                    select
                    size="small"
                    fullWidth
                    variant="outlined"
                    label="Recurring"
                    value={schedule.type}
                    onChange={(e) => setSchedule((prev) => withType(prev, e.target.value, todayKey))}
                >
                    {SCHEDULE_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            {usesCalendar(schedule) ? null : (
                <Grid
                    item
                    css={css`
                        flex: 1 1 auto;
                        min-width: 0;
                    `}
                >
                    <SchedulePicker
                        schedule={schedule}
                        setSchedule={setSchedule}
                        todayKey={todayKey}
                        error={scheduleError}
                    />
                </Grid>
            )}
        </Grid>

        {usesCalendar(schedule) ? (
            <div
                css={css`
                    margin: -8px 0 20px;
                `}
            >
                <SchedulePicker
                    schedule={schedule}
                    setSchedule={setSchedule}
                    todayKey={todayKey}
                    error={scheduleError}
                />
            </div>
        ) : null}

        {skipsShortMonths(schedule) ? (
            <SectionSubheader css={WARNING}>
                Months without that day are skipped — a resource on the 31st doesn&apos;t run in months that end on the
                30th.
            </SectionSubheader>
        ) : null}

        {schedule.mixed ? (
            <SectionSubheader css={WARNING}>
                This resource&apos;s schedule mixes recurrences this form can&apos;t show together. Changing it will
                replace the whole schedule.
            </SectionSubheader>
        ) : null}
    </LocalizationProvider>
);

export default ScheduleSection;
